<script setup>
// Collapsible "Check-in details" accordion + blackout banner used inside the
// full-screen QR takeover (dark styling variant).
// Port of `QrDetails` in the source `_shared/CheckInSheet.tsx`.
import { ref, computed } from 'vue'
import { AlertTriangle, Info, ChevronDown, Clock } from '@lucide/vue'
import { hasDetailsContent, perLabel } from './checkin'

const props = defineProps({
  details: { type: Object, default: null },
})

const open = ref(false)

const hasContent = computed(() => hasDetailsContent(props.details))
const visible = computed(() => props.details && (hasContent.value || props.details.isBlackoutToday))
const frequency = computed(() => props.details?.frequency || null)
const usedCount = computed(() => frequency.value?.usedCount ?? 0)
const remaining = computed(() => (frequency.value ? Math.max(0, frequency.value.cap - usedCount.value) : 0))
</script>

<template>
  <div v-if="visible" class="cs-qr-details">
    <div v-if="details.isBlackoutToday" class="cs-qr-details__blackout">
      <AlertTriangle class="cs-qr-details__blackout-icon" :size="14" />
      <div class="cs-qr-details__blackout-text">
        <span class="cs-qr-details__blackout-label">Blackout today</span>
        <span class="cs-qr-details__blackout-sub">— check-in isn't available today.</span>
      </div>
    </div>

    <div v-if="hasContent" class="cs-qr-details__box">
      <button
        id="checkin-qr-details-toggle"
        type="button"
        class="cs-qr-details__toggle gtm-click"
        :aria-expanded="open"
        @click="open = !open"
      >
        <span class="cs-qr-details__toggle-label">
          <Info :size="14" /> Check-in details
        </span>
        <ChevronDown class="cs-qr-details__chevron" :class="{ 'cs-qr-details__chevron--open': open }" :size="14" />
      </button>

      <div v-if="open" class="cs-qr-details__content">
        <p v-if="details.description" class="cs-qr-details__description">{{ details.description }}</p>

        <div v-if="details.howToUse?.length">
          <div class="cs-qr-details__section-label">How to use</div>
          <div class="cs-qr-details__steps">
            <p v-for="(s, i) in details.howToUse" :key="i" class="cs-qr-details__step">{{ s }}</p>
          </div>
        </div>

        <div v-if="frequency">
          <div class="cs-qr-details__section-label">Frequency</div>
          <div class="cs-qr-details__frequency">
            Up to <span class="cs-qr-details__frequency-strong">{{ frequency.cap }} check-in{{ frequency.cap === 1 ? '' : 's' }}</span>
            {{ perLabel(frequency.per) }}
            <span class="cs-qr-details__frequency-muted">
              · {{ remaining }} left ({{ usedCount }}/{{ frequency.cap }} used)
            </span>
            <div v-if="frequency.nextAvailableLabel && usedCount >= frequency.cap" class="cs-qr-details__frequency-next">
              <Clock :size="12" /> Next check-in {{ frequency.nextAvailableLabel }}
            </div>
          </div>
        </div>

        <div v-if="details.restrictions?.length">
          <div class="cs-qr-details__section-label">Restrictions</div>
          <ul class="cs-qr-details__restrictions">
            <li v-for="(r, i) in details.restrictions" :key="i" class="cs-qr-details__restriction">
              <span class="cs-qr-details__bullet">•</span>{{ r }}
            </li>
          </ul>
        </div>

        <div v-if="details.blackoutDates?.length">
          <div class="cs-qr-details__section-label">Blackout dates</div>
          <div class="cs-qr-details__chips">
            <span v-for="d in details.blackoutDates" :key="d" class="cs-qr-details__chip">{{ d }}</span>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<style lang="scss" scoped>
@use './checkin-shared' as *;

// Source: `mt-3 space-y-2 text-left`
.cs-qr-details {
  margin-top: 12px; // mt-3
  text-align: left;

  > * + * {
    margin-top: 8px; // space-y-2
  }

  // Source: `flex items-start gap-2 rounded-xl bg-rose-500/20 px-3 py-2
  //          text-rose-100 ring-1 ring-rose-400/40`
  &__blackout {
    display: flex;
    align-items: flex-start;
    gap: 8px;
    border-radius: 12px;
    background: rgba($cs-rose-500, 0.2);
    padding: 8px 12px;
    color: $cs-rose-100;
    box-shadow: 0 0 0 1px rgba($cs-rose-400, 0.4);
  }

  &__blackout-icon {
    margin-top: 2px; // mt-0.5
    flex-shrink: 0;
  }

  &__blackout-text {
    font-size: 11px;
    line-height: 1.375;
  }

  &__blackout-label {
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.05em;
  }

  &__blackout-sub {
    margin-left: 4px; // ml-1
    opacity: 0.9;
  }

  // Source: `rounded-xl bg-white/5 ring-1 ring-white/10`
  &__box {
    border-radius: 12px;
    background: rgba(255, 255, 255, 0.05);
    box-shadow: 0 0 0 1px rgba(255, 255, 255, 0.1);
  }

  // Source: `flex w-full items-center justify-between gap-2 rounded-xl px-3
  //          py-2 text-white outline-none`
  &__toggle {
    @include cs-button-reset;
    display: flex;
    width: 100%;
    align-items: center;
    justify-content: space-between;
    gap: 8px;
    border-radius: 12px;
    padding: 8px 12px;
    color: #ffffff;
    outline: none;

    &:focus,
    &:focus-visible {
      outline: none;
    }
  }

  // Source: `inline-flex items-center gap-2 text-[11px] font-bold uppercase tracking-wider`
  &__toggle-label {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    font-size: 11px;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.05em;
  }

  // Source: `h-3.5 w-3.5 text-white/70 transition-transform` + `rotate-180` when open
  &__chevron {
    color: rgba(255, 255, 255, 0.7);
    transition: transform 150ms cubic-bezier(0.4, 0, 0.2, 1);
    flex-shrink: 0;

    &--open {
      transform: rotate(180deg);
    }
  }

  // Source: `space-y-2.5 border-t border-white/10 px-3 py-3 text-white/85`
  &__content {
    border-top: 1px solid rgba(255, 255, 255, 0.1);
    padding: 12px; // px-3 py-3
    color: rgba(255, 255, 255, 0.85);

    > * + * {
      margin-top: 10px; // space-y-2.5
    }
  }

  // Source: `text-[12px] leading-snug text-white/70`
  &__description {
    margin: 0;
    font-size: 12px;
    line-height: 1.375;
    color: rgba(255, 255, 255, 0.7);
  }

  // Source: `mb-1 text-[10px] font-bold uppercase tracking-wider text-white/60`
  &__section-label {
    margin-bottom: 4px;
    font-size: 10px;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.05em;
    color: rgba(255, 255, 255, 0.6);
  }

  // Source: `space-y-1.5 text-[12px] leading-snug`
  &__steps {
    font-size: 12px;
    line-height: 1.375;

    > * + * {
      margin-top: 6px; // space-y-1.5
    }
  }

  &__step {
    margin: 0;
    white-space: pre-line;
  }

  // Source: `text-[12px] leading-snug text-white/85`
  &__frequency {
    font-size: 12px;
    line-height: 1.375;
    color: rgba(255, 255, 255, 0.85);
  }

  &__frequency-strong {
    font-weight: 600; // font-semibold
  }

  // Source: `ml-1 text-white/60`
  &__frequency-muted {
    margin-left: 4px;
    color: rgba(255, 255, 255, 0.6);
  }

  // Source: `mt-0.5 inline-flex items-center gap-1 text-white/60`
  &__frequency-next {
    margin-top: 2px;
    display: inline-flex;
    align-items: center;
    gap: 4px;
    color: rgba(255, 255, 255, 0.6);
  }

  // Source: `space-y-1 text-[12px] leading-snug`
  &__restrictions {
    margin: 0;
    padding: 0;
    list-style: none;
    font-size: 12px;
    line-height: 1.375;

    > * + * {
      margin-top: 4px; // space-y-1
    }
  }

  // Source: `flex gap-2`
  &__restriction {
    display: flex;
    gap: 8px;
  }

  &__bullet {
    opacity: 0.6;
  }

  // Source: `flex flex-wrap gap-1.5`
  &__chips {
    display: flex;
    flex-wrap: wrap;
    gap: 6px;
  }

  // Source: `rounded-full bg-white/10 px-2 py-0.5 text-[11px] ring-1 ring-white/10`
  &__chip {
    border-radius: 9999px;
    background: rgba(255, 255, 255, 0.1);
    padding: 2px 8px;
    font-size: 11px;
    box-shadow: 0 0 0 1px rgba(255, 255, 255, 0.1);
  }
}
</style>
